import React from 'react';
import { Button, Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Form, Formik } from 'formik';
import * as yup from 'yup';
import TitledDrawer from '../common/ui/TitledDrawer';
import { LinksService, LinkStatus, QrBoardDTO } from '../api';
import { useSafeExecute } from '../common/apiNotifications/safeExecute';
import { FormTextField } from '../experienceSettings/formComponents/FormTextField';

interface EditQrBoardDrawerProps {
  open: boolean;
  onClose: () => void;
  qrBoard: QrBoardDTO | undefined;
  onUpdate: () => void;
}

export const EditQrBoardDrawer = React.memo(({ open, onClose, qrBoard, onUpdate }: EditQrBoardDrawerProps) => {
  const { t } = useTranslation();
  const safeExecute = useSafeExecute();

  const validationSchema = yup.object().shape({
    long_link: yup.string().url(t('qrBoards.urlInvalid')).required(t('qrBoards.urlRequired')),
    label: yup.string(),
  });

  const handleSubmit = async (values: { long_link: string; label: string }) => {
    if (!qrBoard) return;
    await safeExecute({
      apiCall: () =>
        LinksService.updateQrBoard({
          short_code: qrBoard.short_code,
          long_link: values.long_link,
          label: values.label || undefined,
        }),
      successMessage: t('save_changes'),
    });

    onUpdate();
    onClose();
  };

  return (
    <TitledDrawer title={t('qrBoards.editTitle')} open={open} onClose={onClose}>
      <Formik
        enableReinitialize
        initialValues={{
          long_link: qrBoard?.status === LinkStatus.QR_NOT_CONFIGURED ? '' : qrBoard?.long_link || '',
          label: qrBoard?.label || '',
        }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}>
        {({ isSubmitting }) => (
          <Form style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
            <Grid
              container
              direction="column"
              flexWrap="nowrap"
              gap={1.5}
              sx={{
                flex: 1,
                minHeight: 0,
                overflowY: 'auto',
                pt: { xs: '12px', md: '18px' },
                pb: 2,
                paddingInline: { xs: '12px', md: '24px' },
              }}>
              <FormTextField
                width="100%"
                fieldName="long_link"
                label={t('qrBoards.destinationUrl')}
                placeholder="https://example.com"
                isFieldUpdate
              />
              <FormTextField width="100%" fieldName="label" label={t('qrBoards.label')} isFieldUpdate />
            </Grid>
            <Grid
              container
              justifyContent="flex-end"
              sx={{
                flexShrink: 0,
                py: 1.5,
                paddingInline: { xs: '12px', md: '24px' },
                borderTop: '1px solid',
                borderColor: 'mb.lineSoft',
              }}>
              <Button type="submit" variant="contained" color="primary" disabled={isSubmitting}>
                {t('save')}
              </Button>
            </Grid>
          </Form>
        )}
      </Formik>
    </TitledDrawer>
  );
});
